/** Spacing, radius, breakpoints and stacking order. */

/**
 * A 4px base. Steps are named by their multiple, so `space[6]` is 24px and the arithmetic stays
 * visible — half the point of a scale is that a reviewer can tell 24 from 20 without a lookup.
 */
export const space = {
  0: '0px',
  px: '1px',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem',
} as const;

/**
 * Dashboard density. Comfortable, not airy — and stated here rather than decided per screen,
 * which is how card padding drifted between features in the first place.
 *
 * Every value is a step from `space` above; nothing here invents a measurement. The rule is that
 * a screen composes from these six and reaches into `space` only for something genuinely local
 * (the gap between an icon and its label, say).
 *
 * The two control heights are a real distinction, not a hedge: a filter toolbar is scanned and
 * clicked through quickly and wants 40px, while a form dialog is typed into carefully and keeps
 * the 48px that lines a field up with a `large` button beside it. Using one height for both makes
 * either the toolbar bloated or the form cramped.
 */
export const density = {
  /** Page padding. Drops a step below `md`, where the rail has already become a drawer. */
  pageGutter: '1.5rem',
  pageGutterNarrow: '1rem',
  /** Between the major rows of a dashboard — metrics, chart, structure, attention. */
  sectionGap: '1.5rem',
  /** Between cards inside one row. Tighter than the section gap, so rows group visually. */
  cardGap: '1rem',
  /** Inside a card. */
  cardPadding: '1.25rem',
  cardPaddingTight: '1rem',
  /** Filter toolbars, table header controls, chips. */
  controlHeight: '2.5rem',
  /** Form fields inside a dialog. */
  controlHeightComfortable: '3rem',
  /** Table and grid rows. 48px: denser than the POS's 64, because this is read seated. */
  rowHeight: '3rem',
  rowHeightDense: '2.25rem',
} as const;

/**
 * Generous, as the brief asks. `lg` is the house default for cards and buttons; `xl` and above
 * are for the POS, where a large soft corner is most of what makes a web view stop feeling like
 * a form. `card` and `control` are aliases so a radius change is a decision, not a sweep.
 */
export const radius = {
  none: '0px',
  xs: '0.375rem',
  sm: '0.625rem',
  md: '0.875rem',
  lg: '1.25rem',
  xl: '1.75rem',
  '2xl': '2.25rem',
  full: '9999px',
  card: '1.25rem',
  control: '1.25rem',
  sheet: '1.75rem',
  chip: '9999px',
} as const;

/**
 * Shared breakpoints. The POS uses two: `md`, where it splits into search and cart side by side
 * and fills the screen; and `lg`, where it stops growing, caps at `posColumn.wide`, and sits on
 * a backdrop. Below `md` it is one column, edge to edge.
 */
export const breakpoint = {
  sm: '480px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1600px',
} as const;

/**
 * The POS portrait column. Fixed rather than fluid: the muscle memory has to be identical on a
 * counter laptop and a phone, which means the tap targets land in the same place relative to
 * each other at every viewport.
 */
export const posColumn = {
  width: '30rem',
  minWidth: '20rem',
  /**
   * The two-panel surface's cap, from `lg` up. Exists only so the app does not stretch absurdly
   * wide on a large monitor; a tablet below `lg` fills its screen instead.
   */
  wide: '1100px',
  /**
   * Below this the column is the viewport and the backdrop is not drawn.
   *
   * In px, not rem, and deliberately: `rem` inside a media query resolves against the browser's
   * initial 16px root, never the document's — so a `32rem` breakpoint in the POS, whose root is
   * 17px, would fire 32px earlier than the same number anywhere else in the file.
   */
  breakpoint: '512px',
} as const;

/**
 * Dialog widths. Two, and only two — see `AppDialog` in the dashboard for the rule that picks
 * between them. The pair is stated here rather than per dialog because "this one needs a little
 * more room" is exactly how a product ends up with nine dialog widths and no reason for any of
 * them.
 */
export const dialogWidth = {
  /** Every dialog. A form, a confirmation, a picker. */
  default: '480px',
  /** Only a dialog whose content is a table, which cannot be read at 480 without scrolling. */
  table: '600px',
} as const;

/** Minimum touch target. 48px, not 44 — the counter is a hurried, one-handed environment. */
export const touchTarget = {
  min: '3rem',
  comfortable: '3.5rem',
  keypad: '4.5rem',
  /**
   * The floor for a small, secondary control, and the only value here stated in px.
   *
   * In rem it would not be 44px anywhere it matters: the POS root is 17px, so the `2.5rem` this
   * replaces measured 42.5 and rendered 43 — a pixel under the accessibility minimum, on every
   * small button in the app, which is exactly the kind of miss nobody finds by looking. A px
   * value is 44px whatever the root does.
   *
   * It is a floor for controls that cannot be `min`, not a licence to use it instead.
   */
  compact: '44px',
} as const;

/**
 * One ladder, so nothing is ever fixed by adding a nine. Dialogs sit above toasts deliberately:
 * a confirmation must not be obscured by a notification about the thing it is confirming.
 */
export const zIndex = {
  base: 0,
  raised: 10,
  sticky: 100,
  header: 200,
  drawer: 300,
  scrim: 400,
  dialog: 500,
  toast: 600,
  tooltip: 700,
  /** Offline banner and DEVICE_REVOKED — above everything, including a modal mid-edit. */
  critical: 800,
} as const;
